import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PublicLayout from "../components/layout/PublicLayout";
import { api, formatMoney, formatApiError } from "../lib/api";
import PriceDisplay from "../components/PriceDisplay";
import { useCurrency } from "../context/CurrencyContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import { carImageUrl } from "../components/cars/CarCard";
import { CreditCard, Smartphone, ShieldCheck, Info, ArrowRight, Building2 } from "lucide-react";

export default function Checkout() {
  const { bookingId } = useParams();
  const nav = useNavigate();
  const { currencies, selectedCurrency } = useCurrency();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState("mtn_momo");
  const [momoPhone, setMomoPhone] = useState("");
  const [momoNetwork, setMomoNetwork] = useState("mtn");
  const [paymentCurrency, setPaymentCurrency] = useState("RWF"); // card payments only — USD or RWF
  const [submitting, setSubmitting] = useState(false);
  const [publicSettings, setPublicSettings] = useState(null);

  useEffect(() => {
    api.get(`/bookings/${bookingId}`)
      .then(({ data }) => setBooking(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
    api.get(`/settings/public`).then(({ data }) => setPublicSettings(data)).catch(() => {});
  }, [bookingId]);

  const channelMap = { mtn_momo: "MOMO", airtel: "AIRTEL_MONEY", card: "CARD" };

  const pay = async () => {
    const channelName = channelMap[method];
    let normalizedPhone = "";
    if (method === "mtn_momo" || method === "airtel") {
      if (!momoPhone) {
        toast.error(method === "mtn_momo" ? "Please enter your MTN MoMo phone number" : "Please enter your Airtel Money phone number");
        return;
      }
      normalizedPhone = momoPhone.trim().replace(/\s/g, "");
      if (normalizedPhone.startsWith("0")) normalizedPhone = "+250" + normalizedPhone.slice(1);
      else if (normalizedPhone.startsWith("250")) normalizedPhone = "+" + normalizedPhone;
      else if (!normalizedPhone.startsWith("+")) normalizedPhone = "+250" + normalizedPhone;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        booking_id: bookingId,
        origin_url: window.location.origin,
        payment_method: "urubutopay",
        channel_name: channelName,
        phone_number: normalizedPhone,
        payment_currency: method === "card" ? paymentCurrency : undefined,
      });
      if (channelName === "CARD" && data.card_processing_url) {
        window.location.href = data.card_processing_url;
        return;
      }
      toast.info(data.message || "Payment request sent!");
      window.location.href = `/booking/${bookingId}/success?session_id=${data.session_id}`;
    } catch (e) {
      toast.error(formatApiError(e));
      setSubmitting(false);
    }
  };

  if (loading) return <PublicLayout><div className="max-w-5xl mx-auto py-20 text-center text-muted-foreground">Loading…</div></PublicLayout>;
  if (!booking) return <PublicLayout><div className="max-w-5xl mx-auto py-20 text-center">Booking not found.</div></PublicLayout>;

  return (
    <PublicLayout>
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.2em] text-accent font-bold mb-2">Checkout</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">Complete your booking</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Left: payment */}
          <div className="md:col-span-7 space-y-6">
            <Card className="border-border shadow-organic p-6" data-testid="escrow-info-card">
              <div className="flex items-start gap-2.5">
                <ShieldCheck size={18} className="text-primary mt-0.5 shrink-0" />
                <div>
                  <div className="font-display font-semibold text-sm">How payment works</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Pay the <strong>{(booking.commission_rate * 100).toFixed(0)}% booking fee</strong> (<PriceDisplay amount={booking.commission} listingCurrency={booking.currency} />) now via MTN MoMo to confirm your booking and unlock the owner contact. Pay the remaining <strong><PriceDisplay amount={booking.owner_net} listingCurrency={booking.currency} /></strong> directly to the owner at pickup.
                  </div>
                </div>
              </div>
            </Card>

            <Card className="border-border shadow-organic p-6 space-y-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Payment method</div>
              <div onClick={() => setMethod("mtn_momo")}
                className={"p-4 rounded-xl border-2 cursor-pointer transition-colors " + (method === "mtn_momo" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40")}>
                <div className="flex items-center gap-3 mb-1">
                  <Smartphone size={16} className="text-primary" />
                  <div className="font-semibold text-sm">MTN Mobile Money</div>
                  <span className="ml-auto text-[10px] bg-primary text-white px-2 py-0.5 rounded-full">Available</span>
                </div>
                {method === "mtn_momo" && (
                  <div className="mt-2">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">MTN MoMo Phone Number</Label>
                    <Input data-testid="momo-phone-input" placeholder="+250 78x xxx xxx" value={momoPhone} onChange={e => setMomoPhone(e.target.value)} className="mt-1.5 h-10" />
                    <div className="text-[10px] text-muted-foreground mt-1">Include country code e.g. +250788123456</div>
                  </div>
                )}
              </div>
              <div onClick={() => setMethod("airtel")}
                className={"p-4 rounded-xl border-2 cursor-pointer transition-colors " + (method === "airtel" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40")}>
                <div className="flex items-center gap-3 mb-1">
                  <Smartphone size={16} className="text-red-500" />
                  <div className="font-semibold text-sm">Airtel Money</div>
                  <span className="ml-auto text-[10px] bg-primary text-white px-2 py-0.5 rounded-full">Available</span>
                </div>
                {method === "airtel" && (
                  <div className="mt-2">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Airtel Money Phone Number</Label>
                    <Input placeholder="+250 73x xxx xxx" value={momoPhone} onChange={e => setMomoPhone(e.target.value)} className="mt-1.5 h-10" />
                    <div className="text-[10px] text-muted-foreground mt-1">Include country code e.g. +250738123456</div>
                  </div>
                )}
              </div>
              <div onClick={() => setMethod("card")}
                className={"p-4 rounded-xl border-2 cursor-pointer transition-colors " + (method === "card" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40")}>
                <div className="flex items-center gap-3">
                  <CreditCard size={16} className="text-blue-500" />
                  <div className="font-semibold text-sm">ATM / Bank Card</div>
                  <span className="ml-auto text-[10px] bg-primary text-white px-2 py-0.5 rounded-full">Available</span>
                </div>
                {method === "card" && (
                  <div className="mt-2 space-y-2">
                    <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                      {["RWF", "USD"].map((c) => (
                        <button key={c} type="button" onClick={() => setPaymentCurrency(c)}
                          className={"px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors " + (paymentCurrency === c ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:bg-muted")}>
                          Pay in {c}
                        </button>
                      ))}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      You will be redirected to a secure page to enter your card details.
                    </div>
                  </div>
                )}
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-1.5 pt-1">
                <ShieldCheck size={12} className="text-primary" />
                Your payment is securely processed.
              </div>
            </Card>
          </div>

          {/* Right: order summary */}
          <aside className="md:col-span-5">
            <Card className="border-border shadow-organic p-6 md:sticky md:top-24" data-testid="order-summary-card">
              <div className="flex gap-3">
                <div className="w-20 h-20 rounded-md overflow-hidden bg-muted border border-border">
                  <img src={carImageUrl(booking.car_image)} alt={booking.car_title} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold truncate">{booking.car_title}</div>
                  <div className="text-xs text-muted-foreground">{booking.start_date} → {booking.end_date}</div>
                  <Badge variant="outline" className="text-[10px] mt-1.5">{booking.days} day{booking.days > 1 ? "s" : ""}</Badge>
                </div>
              </div>
              <Separator className="my-5" />
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span>Total rental cost</span>
                  <span><PriceDisplay amount={booking.total} listingCurrency={booking.currency} /></span>
                </div>
                <div className="flex justify-between font-display text-lg font-bold text-primary">
                  <span>Booking fee due now</span>
                  <span data-testid="checkout-amount-now"><PriceDisplay amount={booking.commission} listingCurrency={booking.currency} /></span>
                </div>
                <div className="flex justify-between text-muted-foreground text-xs">
                  <span>Remaining balance (pay to owner at pickup)</span>
                  <span><PriceDisplay amount={booking.owner_net} listingCurrency={booking.currency} /></span>
                </div>
                <div className="text-xs text-muted-foreground mt-1 p-2 bg-muted rounded-lg">
                  You only pay the {(booking.commission_rate * 100).toFixed(0)}% booking fee now. The remaining amount is paid directly to the owner when you pick up the car.
                </div>
              </div>


              <Button
                onClick={pay}
                disabled={submitting}
                className="mt-6 w-full h-11 bg-accent hover:bg-accent/90 text-accent-foreground btn-lift"
                data-testid="pay-now-btn"
              >
                {submitting ? "Sending payment request…" : "Pay booking fee"}
                <ArrowRight size={16} className="ml-1.5" />
              </Button>
              <Button variant="ghost" className="w-full mt-2" onClick={() => nav(-1)} data-testid="back-btn">
                Back
              </Button>


            </Card>
          </aside>
        </div>
      </div>
    </PublicLayout>
  );
}
